import React from 'react';
import {Editor, Transforms, Element as SlateElement, Range} from 'slate';
import {HistoryEditor} from 'slate-history';

import showMessage from '../../../utils/showMessage';

interface ShortcutProps {
  editor: Editor;
  saveDocument: () => void;
  handlePrint: () => void;
  toggleMark: (format: string) => void;
  insertLink: () => void;
  insertComment?: () => void;
  toggleCommentPanel?: () => void;
  clearFormatting: () => void;
  setAlignment: (align: string) => void;
  setFontSize: (size: number) => void;
  currentFontSize: number;
  indentList: (editor: Editor) => void;
  outdentList: (editor: Editor) => void;
}

export const useKeyboardShortcuts = ({
  editor,
  saveDocument,
  handlePrint,
  toggleMark,
  insertLink,
  insertComment,
  toggleCommentPanel,
  clearFormatting,
  setAlignment,
  setFontSize,
  currentFontSize,
  indentList,
  outdentList
}: ShortcutProps): ((event: React.KeyboardEvent) => boolean) => {
  return (event: React.KeyboardEvent): boolean => {
    if (event.key === 'Tab') {
      const indentableBlock = Editor.nodes(editor, {
        match: (n) =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          ['list-item', 'paragraph'].includes((n as any).type)
      }).next().value;
      if (indentableBlock) {
        event.preventDefault();
        if (event.shiftKey) outdentList(editor);
        else indentList(editor);

        return true;
      }
    }

    if (event.ctrlKey || event.metaKey) {
      const key = event.key.toLowerCase();

      // Comments: Cmd/Ctrl + Alt/Option + M — new comment
      if (
        event.altKey &&
        key === 'm' &&
        insertComment &&
        editor.selection &&
        !Range.isCollapsed(editor.selection)
      ) {
        event.preventDefault();
        insertComment();

        return true;
      }

      // Comments: Cmd/Ctrl + Shift + C — toggle comments panel
      if (event.shiftKey && key === 'c' && toggleCommentPanel) {
        event.preventDefault();
        toggleCommentPanel();

        return true;
      }

      // System Actions

      if (key === 's') {
        event.preventDefault();
        saveDocument();

        return true;
      }
      if (key === 'p') {
        event.preventDefault();
        handlePrint();

        return true;
      }

      // History
      if (key === 'z') {
        event.preventDefault();
        try {
          if (event.shiftKey) HistoryEditor.redo(editor);
          else HistoryEditor.undo(editor);
        } catch (e) {
          showMessage({
            status: 'warning',
            message:
              'Could not ' +
              (event.shiftKey ? 'redo' : 'undo') +
              ' the last action.'
          });
        }

        return true;
      }
      if (key === 'y') {
        event.preventDefault();
        try {
          HistoryEditor.redo(editor);
        } catch (e) {
          showMessage({
            status: 'warning',
            message: 'Could not redo the last action.'
          });
        }

        return true;
      }

      // Select All
      if (key === 'a') {
        event.preventDefault();
        const start = Editor.start(editor, []);
        const end = Editor.end(editor, []);
        Transforms.select(editor, {anchor: start, focus: end});

        return true;
      }

      // Formatting (Require Selection)
      if (editor.selection) {
        if (key === 'b') {
          event.preventDefault();
          toggleMark('bold');

          return true;
        }
        if (key === 'i') {
          event.preventDefault();
          toggleMark('italic');

          return true;
        }
        if (key === 'u') {
          event.preventDefault();
          toggleMark('underline');

          return true;
        }
        if (key === 'k') {
          event.preventDefault();
          insertLink();

          return true;
        }
        if (event.key === '\\') {
          event.preventDefault();
          clearFormatting();

          return true;
        }

        // Font Size
        if (event.key === '[' || event.key === ']') {
          event.preventDefault();
          const delta = event.key === ']' ? 1 : -1;
          setFontSize(Math.max(1, currentFontSize + delta));

          return true;
        }

        // Alignment
        if (event.shiftKey) {
          if (key === 'l') {
            event.preventDefault();
            setAlignment('left');

            return true;
          }
          if (key === 'e') {
            event.preventDefault();
            setAlignment('center');

            return true;
          }
          if (key === 'r') {
            event.preventDefault();
            setAlignment('right');

            return true;
          }
          if (key === 'j') {
            event.preventDefault();
            setAlignment('justify');

            return true;
          }
        }
      }
    }

    // Strikethrough (Alt + Shift + 5)
    if (event.altKey && event.shiftKey && event.key === '5') {
      event.preventDefault();
      toggleMark('strikethrough');

      return true;
    }

    // Ctrl + Enter — Insert Page Break
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      Transforms.insertNodes(editor, [
        {type: 'page-break', children: [{text: ''}]},
        {type: 'paragraph', children: [{text: ''}]}
      ] as any);

      return true;
    }

    // Backspace at the start of a page — delete the page break if it exists
    if (event.key === 'Backspace') {
      const {selection} = editor;
      if (selection && Range.isCollapsed(selection)) {
        const blockEntry = Editor.above(editor, {
          match: (n) =>
            SlateElement.isElement(n) && Editor.isBlock(editor, n as any)
        });
        if (blockEntry) {
          const [, path] = blockEntry as [any, number[]];
          if (Editor.isStart(editor, selection.anchor, path)) {
            const pageIndex = path[0];
            const nodeIndex = path[1];

            if (pageIndex > 0) {
              const currentPage = editor.children[pageIndex] as any;
              const firstContentIndex = currentPage.children.findIndex(
                (c: any) => c.type !== 'header-wrapper'
              );

              if (nodeIndex === firstContentIndex) {
                const prevPage = editor.children[pageIndex - 1] as any;
                // Find the last node index that isn't a footer
                let lastNodeIndex = prevPage.children.length - 1;
                while (
                  lastNodeIndex >= 0 &&
                  prevPage.children[lastNodeIndex].type === 'footer-wrapper'
                ) {
                  lastNodeIndex--;
                }

                if (lastNodeIndex >= 0) {
                  const lastNode = prevPage.children[lastNodeIndex];
                  if (lastNode && lastNode.type === 'page-break') {
                    event.preventDefault();
                    Transforms.removeNodes(editor, {
                      at: [pageIndex - 1, lastNodeIndex]
                    });

                    return true;
                  }
                }
              }
            }
          }
        }
      }
    }

    return false;
  };
};
